// src/pages/CommissionReportSalesRepPage.js
import React, { useEffect, useState } from 'react';
import { getSalesReps, getCommissionReport } from '../api';
import { isComercialAndNotOwnerAdminGestion, getSalesRepId } from '../services/AuthService';
import DatePicker from 'react-datepicker';
import { format } from 'date-fns';
import 'react-datepicker/dist/react-datepicker.css';
import { Form, Button, Spinner } from 'react-bootstrap';
import { es } from 'date-fns/locale';

const CommissionReportSalesRepPage = () => {
  // filtros
  const [salesReps, setSalesReps] = useState([]);
  const [filters, setFilters] = useState({
    salesRepId: isComercialAndNotOwnerAdminGestion() ? getSalesRepId()?.toString() : '',
    dateFrom: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
    dateTo: new Date(),
  });

  // datos
  const [items, setItems] = useState([]);
  const [totalCommission, setTotalCommission] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // cargar comerciales
  useEffect(() => {
    if (!isComercialAndNotOwnerAdminGestion()){
      getSalesReps().then(setSalesReps).catch(console.error);
    }
  }, []);

  // Si es comercial sin permisos, fijamos el filtro y no permitimos cambiarlo
  useEffect(() => {
    if (isComercialAndNotOwnerAdminGestion()) {
        setFilters(f => ({ ...f, salesRepId: getSalesRepId()?.toString() }));
    }
  }, []);

  // fetch datos
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    // Si no hay comercial seleccionado, limpiamos todo y salimos
    if (!filters.salesRepId) {
      setItems([]);
      setTotalCommission(0);
      setLoading(false);
      return;
    }
    try {
      const from = format(filters.dateFrom, 'yyyy-MM-dd');
      const to   = format(filters.dateTo,   'yyyy-MM-dd');
      const res  = await getCommissionReport(
        filters.salesRepId || undefined,
        from,
        to,
      );
      setItems(res.items);
      setTotalCommission(res.totalCommission);
    } catch (err) {
      console.error(err);
      setError('Error al cargar el informe');
    } finally {
      setLoading(false);
    }
  };

  // recarga al cambiar filtros o paginación
  useEffect(() => {
    fetchData();
  }, [filters]);

  if (loading) return <div className="container-fluid my-4"><Spinner animation="border" /> Cargando…</div>;

  return (
    <div className="container-fluid my-4">
      <h2 className="mb-4">Informe de Comisiones</h2>

      {/* Total */}
      <div className="mb-4 d-flex justify-content-center">
        <div className="p-3 bg-success text-white rounded shadow-sm text-center">
          <div className="h6 mb-1">Total Comisión</div>
          <div className="display-6 fw-bold">{totalCommission.toFixed(2)}€</div>
        </div>
      </div>

      {/* filtros */}
      <div className="d-flex flex-wrap gap-3 align-items-end mb-4">
        {!isComercialAndNotOwnerAdminGestion() && (
          <Form.Group>
            <Form.Label>Comercial</Form.Label>
            <Form.Select
              value={filters.salesRepId}
              onChange={e => setFilters(f => ({ ...f, salesRepId: e.target.value }))}
            >
              <option value="">Seleccionar comercial</option>
              {salesReps.map(sr => (
                <option key={sr.id} value={sr.id}>
                  {sr.name} {sr.lastName1}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        )}

        <Form.Group>
          <Form.Label>Desde</Form.Label>
          <DatePicker
            selected={filters.dateFrom}
            onChange={date => setFilters(f => ({ ...f, dateFrom: date }))}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            locale={es}
          />
        </Form.Group>

        <Form.Group>
          <Form.Label>Hasta</Form.Label>
          <DatePicker
            selected={filters.dateTo}
            onChange={date => setFilters(f => ({ ...f, dateTo: date }))}
            dateFormat="dd/MM/yyyy"
            className="form-control"
            locale={es}
          />
        </Form.Group>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {/* tabla desktop */}
      <div className="table-responsive d-none d-md-block">
        <table className="table table-striped">
          <thead>
            <tr>
              <th>Pedido</th>
              <th>Fecha Pedido</th>
              <th>Entregado</th>
              <th>Artículo</th>
              <th>Modelo</th>
              <th>Color</th>
              <th>Tamaño</th>
              <th>Cant.</th>
              <th>Precio Uni.</th>
              <th>% Comisión</th>
              <th>Modif. %</th>
              <th>Comisión %</th>
              <th>Modif. Fijo</th>
              <th>Comisión Fija</th>
            </tr>
          </thead>
          <tbody>
            {items.map(row => (
              <tr key={row.orderItemId}>
                <td>
                  <a
                    href={`/orders/${row.orderId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {row.externalId}
                  </a>
                </td>
                <td>{format(new Date(row.orderDate), 'dd/MM/yyyy HH:mm')}</td>
                <td>{format(new Date(row.deliveredTime), 'dd/MM/yyyy HH:mm')}</td>
                <td>{row.productTypeName}</td>
                <td>{row.modelName}</td>
                <td>{row.colorName || ''}</td>
                <td>{row.sizeName || ''}</td>
                <td>{row.quantity}</td>
                <td>{row.unitPrice.toFixed(2)}€</td>
                <td>{(row.appliedCommissionPercentage).toFixed(2)}%</td>
                <td>{row.priceModifier.toFixed(2)}%</td>
                <td>{row.commissionAmount.toFixed(2)}€</td>
                <td>{row.fixedModifier.toFixed(2)}€</td>
                <td>{row.fixedCommissionAmount.toFixed(2)}€</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* tarjetas móvil */}
      <div className="d-block d-md-none">
        {items.map(row => (
          <div key={row.orderItemId} className="card mb-2">
            <div className="card-body">
              <p>
                <strong>Pedido:</strong>{' '}
                <a
                  href={`/orders/${row.orderId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {row.externalId}
                </a>
              </p>
              <p><strong>Fecha Ped:</strong> {format(new Date(row.orderDate), 'dd/MM/yyyy')}</p>
              <p><strong>Entregado:</strong> {format(new Date(row.deliveredTime), 'dd/MM/yyyy')}</p>
              <p><strong>Artículo:</strong> {row.productTypeName} – {row.modelName}</p>
              <p><strong>Color:</strong> {row.colorName || ''}</p>
              <p><strong>Tamaño:</strong> {row.sizeName || ''}</p>
              <p><strong>Cantidad:</strong> {row.quantity}</p>
              <p><strong>Precio U:</strong> {row.unitPrice.toFixed(2)}€</p>
              <p><strong>% Com:</strong> {(row.appliedCommissionPercentage).toFixed(2)}%</p>
              <p><strong>Modif. %:</strong> {row.priceModifier.toFixed(2)}%</p>
              <p><strong>Comisión %:</strong> {row.commissionAmount.toFixed(2)}€</p>
              <p><strong>Modif. Fijo:</strong> {row.fixedModifier.toFixed(2)}€</p>
              <p><strong>Comisión Fija:</strong> {row.fixedCommissionAmount.toFixed(2)}€</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default CommissionReportSalesRepPage;
